// src/features/staff/staff.api.ts
import { baseApi } from '@/services/base.api'
import type {
  PermissionsResponse,
  CreateStaffRequest,
  CreateStaffResponse,
  StaffListResponse,
  StaffListParams,
  StaffDetailResponse,
  UpdateStaffRequest,
  UpdateStaffResponse,
  DeactivateStaffResponse,
} from './staff.types'
import { toast } from '@/utils/toast'

export const staffApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getStaffPermissions: builder.query<PermissionsResponse, void>({
      query: () => ({
        url: '/auth/staff/permissions',
        method: 'GET',
      }),
      providesTags: ['Staff'],
    }),
    getStaffPermissionScope: builder.query<PermissionsResponse, void>({
      query: () => ({
        url: '/auth/staff/permissions/scope',
        method: 'GET',
      }),
      providesTags: ['Staff'],
    }),
    getStaff: builder.query<StaffListResponse, StaffListParams>({
      query: ({ page, size, search, status, sortBy = 'createdAt', sortDirection = 'desc' }) => {
        const params: Record<string, any> = {
          page,
          size,
          sortBy,
          sortDirection,
        }
        if (search && search.trim()) params['search'] = search.trim()
        if (status) params['status'] = status
        return {
          url: '/auth/staff',
          method: 'GET',
          params,
        }
      },
      providesTags: ['Staff'],
    }),
    getStaffById: builder.query<StaffDetailResponse, number>({
      query: (id) => ({
        url:    `/auth/staff/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: 'Staff', id }
      ],
    }),
    updateStaff: builder.mutation<
      UpdateStaffResponse,
      { id: number; body: UpdateStaffRequest }
    >({
      query: ({ id, body }) => ({
        url:    `/auth/staff/${id}`,
        method: 'PUT',
        data:   body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Staff',
        { type: 'Staff', id }
      ],
    }),
    deactivateStaff: builder.mutation<DeactivateStaffResponse, number>({
      query: (id) => ({
        url:    `/auth/staff/${id}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Staff',
        { type: 'Staff', id }
      ],
    }),
    reactivateStaff: builder.mutation<DeactivateStaffResponse, number>({
      query: (id) => ({
        url:    `/auth/staff/${id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Staff',
        { type: 'Staff', id }
      ],
    }),
    createStaff: builder.mutation<CreateStaffResponse, CreateStaffRequest>({
      query: (body) => ({
        url: '/auth/staff',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Staff'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message || 'Staff account created successfully')
        } catch {
          // Handled inside component catch block
        }
      },
    }),
  }),
})

export const {
  useGetStaffPermissionsQuery,
  useGetStaffPermissionScopeQuery,
  useGetStaffQuery,
  useGetStaffByIdQuery,
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeactivateStaffMutation,
  useReactivateStaffMutation,
} = staffApi
